import { useEffect, useState } from "react";
import {
  Clock, Play, RefreshCw, Loader2, CheckCircle2, XCircle,
  Timer, History, Trash2, Hand, Zap, Plus, Settings, Globe, Server,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import CronJobModal from "@/components/admin/CronJobModal";

function formatDateTime(iso) {
  if (!iso) return "Jamais";
  const d = new Date(iso);
  const today = new Date();
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `Aujourd'hui à ${time}`;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} à ${time}`;
}

function formatRelative(iso) {
  if (!iso) return null;
  const mins = Math.round((new Date(iso) - new Date()) / 60000);
  if (mins <= 0) return "imminent";
  if (mins < 60) return `dans ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `dans ${hours} h`;
  return `dans ${Math.round(hours / 24)} j`;
}

function describeSchedule(job) {
  if (job.schedule_type === "cron") return job.cron_expression;
  if (job.schedule_type === "daily") return `tous les jours à ${job.daily_time}`;
  const m = job.interval_minutes;
  if (!m) return "—";
  if (m < 60) return `toutes les ${m} min`;
  if (m === 60) return "toutes les heures";
  if (m < 1440) return `toutes les ${m / 60} h`;
  return `tous les ${m / 1440} j`;
}

function StatusBadge({ status, httpStatus }) {
  if (status === "success") {
    return (
      <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/10 font-mono text-[10px] gap-1">
        <CheckCircle2 className="w-3 h-3" /> {httpStatus ? `${httpStatus} OK` : "Succès"}
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10 font-mono text-[10px] gap-1">
        <XCircle className="w-3 h-3" /> {httpStatus || "Erreur"}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-zinc-700 text-zinc-500 font-mono text-[10px]">
      Jamais exécutée
    </Badge>
  );
}

export default function CronContent({
  cronJobs, cronEvents, isLoadingCron, runningJobId,
  fetchCron, updateCronJob, runCronJob, clearCronEvents,
  createCronJob, deleteCronJob, testCronJob, previewCronSchedule,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchCron();
    const interval = setInterval(() => fetchCron(true), 30000);
    return () => clearInterval(interval);
  }, [fetchCron]);

  const openCreate = () => { setEditingJob(null); setModalOpen(true); };
  const openEdit = (job) => { setEditingJob(job); setModalOpen(true); };

  const systemJobs = cronJobs.filter((j) => j.type !== "http");
  const httpJobs = cronJobs.filter((j) => j.type === "http");
  const activeJobs = cronJobs.filter((j) => j.enabled);
  const nextRuns = [...activeJobs]
    .filter((j) => j.next_run)
    .sort((a, b) => new Date(a.next_run) - new Date(b.next_run))
    .slice(0, 6);

  if (isLoadingCron && cronJobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
      </div>
    );
  }

  const renderJob = (job) => (
    <div
      key={job.job_id}
      className={`bg-zinc-900/40 border rounded-xl p-4 transition-colors ${
        job.enabled ? "border-zinc-700/60" : "border-zinc-800/60 opacity-70"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {job.type === "http"
              ? <Globe className="w-3.5 h-3.5 text-cyan-400/70 shrink-0" />
              : <Server className="w-3.5 h-3.5 text-zinc-500 shrink-0" />}
            <p className="text-white font-medium text-sm truncate">{job.name}</p>
          </div>
          <p className="text-zinc-500 text-xs mt-0.5 leading-relaxed break-all">{job.description}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
            <span className="text-amber-400/80 font-mono text-[10px]">{describeSchedule(job)}</span>
            {job.type === "http" && (
              <span className="text-zinc-600 font-mono text-[10px]">{job.method}</span>
            )}
            {job.enabled && job.next_run && (
              <span className="text-zinc-500 font-mono text-[10px]">→ {formatRelative(job.next_run)}</span>
            )}
          </div>
        </div>
        <Switch
          checked={job.enabled}
          onCheckedChange={(checked) => updateCronJob(job.job_id, { enabled: checked })}
          className="shrink-0 mt-0.5"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3.5">
        <Button
          onClick={() => runCronJob(job.job_id)}
          disabled={runningJobId === job.job_id}
          variant="outline" size="sm"
          className="border-zinc-700 text-zinc-300 hover:text-amber-400 hover:border-amber-500/30 hover:bg-amber-500/5 font-mono text-xs h-8"
        >
          {runningJobId === job.job_id
            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <Play className="w-3.5 h-3.5 mr-1.5" />}
          Exécuter
        </Button>
        <Button
          onClick={() => openEdit(job)}
          variant="outline" size="sm"
          className="border-zinc-700 text-zinc-300 hover:text-amber-400 hover:border-amber-500/30 font-mono text-xs h-8"
        >
          <Settings className="w-3.5 h-3.5 mr-1.5" /> Configurer
        </Button>
        {job.type === "http" && (
          confirmDelete === job.job_id ? (
            <div className="flex items-center gap-1.5">
              <Button
                onClick={async () => { await deleteCronJob(job.job_id); setConfirmDelete(null); }}
                size="sm"
                className="bg-red-500 hover:bg-red-600 text-white font-mono text-xs h-8"
              >
                Confirmer
              </Button>
              <Button
                onClick={() => setConfirmDelete(null)}
                variant="ghost" size="sm"
                className="text-zinc-500 font-mono text-xs h-8"
              >
                Annuler
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setConfirmDelete(job.job_id)}
              variant="outline" size="sm"
              className="border-zinc-700 text-red-400/70 hover:text-red-400 hover:border-red-500/30 font-mono text-xs h-8"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 pt-3 border-t border-zinc-800/70">
        <StatusBadge status={job.last_status} httpStatus={job.last_http_status} />
        <span className="text-zinc-500 font-mono text-[10px]">{formatDateTime(job.last_run)}</span>
        {job.last_duration_ms != null && (
          <span className="text-zinc-600 font-mono text-[10px]">{job.last_duration_ms} ms</span>
        )}
        {job.last_message && (
          <span className="text-zinc-500 text-[11px] w-full sm:w-auto break-words">{job.last_message}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500 shrink-0" />
            Tâches planifiées
          </h2>
          <p className="text-zinc-500 text-xs mt-0.5">
            {activeJobs.length} active{activeJobs.length > 1 ? "s" : ""} sur {cronJobs.length}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            onClick={() => fetchCron()}
            variant="ghost" size="sm"
            className="text-zinc-400 hover:text-amber-400"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingCron ? "animate-spin" : ""}`} />
          </Button>
          <Button
            onClick={openCreate}
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black font-mono text-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Nouvelle
          </Button>
        </div>
      </div>

      {/* Prochaines exécutions */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/60">
          <h3 className="font-mono text-xs uppercase tracking-wide text-amber-400/90 flex items-center gap-1.5">
            <Timer className="w-3.5 h-3.5" /> Prochaines exécutions
          </h3>
        </div>
        {nextRuns.length === 0 ? (
          <p className="px-4 py-5 text-zinc-500 text-sm text-center">Aucune tâche active.</p>
        ) : (
          <div className="divide-y divide-zinc-800/70">
            {nextRuns.map((job) => (
              <div key={job.job_id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <span className="text-zinc-300 text-sm truncate">{job.name}</span>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-amber-400 font-mono text-xs">{formatDateTime(job.next_run)}</p>
                  <p className="text-zinc-600 font-mono text-[10px]">{formatRelative(job.next_run)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tâches HTTP */}
      <div className="space-y-3">
        <h3 className="font-mono text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5" /> Tâches HTTP
        </h3>
        {httpJobs.length === 0 ? (
          <div className="bg-zinc-900/30 border border-dashed border-zinc-800 rounded-xl px-4 py-6 text-center">
            <p className="text-zinc-500 text-sm">Aucune tâche HTTP.</p>
            <Button
              onClick={openCreate}
              variant="outline" size="sm"
              className="mt-3 border-zinc-700 text-zinc-300 hover:text-amber-400 hover:border-amber-500/30 font-mono text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Créer une tâche
            </Button>
          </div>
        ) : httpJobs.map(renderJob)}
      </div>

      {/* Tâches système */}
      <div className="space-y-3">
        <h3 className="font-mono text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1.5">
          <Server className="w-3.5 h-3.5" /> Maintenance (système)
        </h3>
        {systemJobs.map(renderJob)}
      </div>

      {/* Historique */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/60 flex items-center justify-between gap-2">
          <h3 className="font-mono text-xs uppercase tracking-wide text-amber-400/90 flex items-center gap-1.5">
            <History className="w-3.5 h-3.5" /> Derniers événements
          </h3>
          {cronEvents.length > 0 && (
            <Button
              onClick={clearCronEvents}
              variant="ghost" size="sm"
              className="text-zinc-500 hover:text-red-400 h-7 text-[10px] font-mono"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Vider
            </Button>
          )}
        </div>
        {cronEvents.length === 0 ? (
          <p className="px-4 py-5 text-zinc-500 text-sm text-center">
            Aucune exécution enregistrée pour le moment.
          </p>
        ) : (
          <div className="divide-y divide-zinc-800/70 max-h-[420px] overflow-y-auto">
            {cronEvents.map((ev) => (
              <div key={ev.id} className="px-4 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5 min-w-0">
                    {ev.status === "success"
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      : <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                      <p className="text-zinc-300 text-sm truncate">{ev.job_name}</p>
                      {ev.message && (
                        <p className="text-zinc-500 text-[11px] break-words">{ev.message}</p>
                      )}
                      {ev.response && (
                        <pre className="text-zinc-600 text-[10px] font-mono whitespace-pre-wrap break-all mt-1 max-h-16 overflow-y-auto">
                          {ev.response.slice(0, 300)}
                        </pre>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-zinc-500 font-mono text-[10px]">{formatDateTime(ev.started_at)}</p>
                    <div className="flex items-center justify-end gap-1.5 mt-0.5">
                      <span className="text-zinc-600 font-mono text-[10px]">{ev.duration_ms} ms</span>
                      {ev.trigger === "manual"
                        ? <Hand className="w-3 h-3 text-zinc-600" />
                        : <Zap className="w-3 h-3 text-zinc-600" />}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CronJobModal
        isOpen={modalOpen}
        onOpenChange={setModalOpen}
        job={editingJob}
        onCreate={createCronJob}
        onUpdate={updateCronJob}
        onTest={testCronJob}
        onPreview={previewCronSchedule}
      />
    </div>
  );
}
